import type { BlogPost } from './types';

export const post: BlogPost = {
  slug: 'the-juice-explained',
  title: 'The juice, explained: the cover charge you keep forgetting to count',
  excerpt:
    'Both sides of a coin flip pay less than even money, and that little gap is the entire business model. Here is the math the app hopes you skip.',
  author: 'Sloane',
  date: '2026-07-02',
  category: 'Betting Basics',
  evergreen: true,
  tags: ['vig', 'odds', 'basics', 'line shopping'],
  body: `# The juice, explained: the cover charge you keep forgetting to count

Here is a bar trick. Find any spread bet in your app. Look at the price on one side: -110. Now look at the other side: also -110. Two outcomes, and both of them cost more than they pay.

That gap is the juice. The vig. The book's cut, baked into every price you will ever see, and the single most important number in betting that nobody puts on the graphic.

## The number at the door

The mechanics are simple. At -110 you risk $110 to win $100. If the bet really were a coin flip, a fair price would pay you exactly what you risked. It does not, and the difference is the cover charge. The book collects it whether your night goes well or not, which is the part worth sitting with. The house does not need to beat you. It charges you to play.

Now the number that should live rent-free in your head: **52.38%**. That is how often you have to win at -110 just to break even. Not 50%. Every bet you place at standard juice starts you from behind by a couple of points, and a couple of points is the whole game. Plenty of sharp, obsessive, spreadsheet-owning people spend their lives fighting for three points of edge. The juice takes two of them at the door.

You can see the same thing in the implied probabilities. Convert both -110 prices to percentages and each side "wins" 52.4% of the time. Add them up: 104.8%. Real universes sum to 100. The extra 4.8% is not a rounding error. It is the margin, and on some markets it gets much worse. Player props and long parlays routinely carry double-digit margins, which is why the book pushes them at you with confetti animations and your straight bets get no love at all.

## Where the margin hides

So what do you actually do with this?

## Make the number useful

First, respect the threshold. If you cannot make a case that your side wins more than 52.4% of the time, you do not have a bet. You have a coin flip you are paying to feel involved in.

Second, notice when the juice moves. -105 instead of -110 sounds like nothing. Across a season of bets it is the difference between a hobby that pays for pizza and one that quietly bills you. [Shopping for the better price](/blog/line-shopping-the-five-boring-minutes-that-pay) is the one edge that requires no talent whatsoever, and almost nobody does it.

Third, stop resenting it. The juice is not a scam. It is the price of admission, printed clearly, on every ticket. The scam is only ever the story you tell yourself about how it does not apply to you.

Frankie says thinking about the vig this much is a personality flaw. She also asked me to check the prices on an eleven-leg slip last Sunday, so the system works.`,
};
